import { useState, type ReactNode } from 'react'
import { Eye, EyeOff, ExternalLink, Loader2, type LucideIcon } from 'lucide-react'
import { cn } from '../../lib/cn'

// Re-export the shared atoms used across the redesigned pages.
export { Stat, Switch, StateChip } from '../conventions/ui'

/* ── page chrome ─────────────────────────────────────────────────── */

export function PageHead({
  title,
  description,
  model,
  children,
}: {
  title: string
  description: ReactNode
  /** When set, renders a "Gestionale" deep-link to the legacy editor for this model. */
  model?: string
  children?: ReactNode
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end gap-4">
      <div>
        <h2 className="font-display text-[26px] font-bold leading-tight tracking-[-.3px] text-coal sm:text-[28px]">
          {title}
        </h2>
        <p className="mt-1.5 max-w-[72ch] text-[13.5px] text-muted">{description}</p>
      </div>
      <div className="flex items-center gap-2.5 sm:ml-auto">
        {children}
        {model && (
          <a
            href={`/admin/yep-admin/content/${model}/list`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-lg border border-line bg-card px-4 py-[10px] text-[13.5px] font-semibold text-slate hover:border-faint"
          >
            <ExternalLink className="h-4 w-4" /> Gestionale
          </a>
        )}
      </div>
    </div>
  )
}

export function SectionCard({
  title,
  icon: Icon,
  desc,
  children,
  aside,
}: {
  title: string
  icon?: LucideIcon
  desc?: ReactNode
  children: ReactNode
  aside?: ReactNode
}) {
  return (
    <section className="cv-panel overflow-hidden">
      <div className="flex items-center gap-2.5 border-b border-line-soft px-5 py-3.5">
        {Icon && (
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-paper text-slate ring-1 ring-line">
            <Icon className="h-4 w-4" />
          </span>
        )}
        <div className="min-w-0">
          <h3 className="font-display text-[15px] font-bold text-coal">{title}</h3>
          {desc && <p className="text-[12px] text-muted">{desc}</p>}
        </div>
        {aside && <div className="ml-auto">{aside}</div>}
      </div>
      <div className="p-5">{children}</div>
    </section>
  )
}

/** Sticky-feeling action bar shown at the bottom of editable forms. */
export function SaveBar({
  dirty,
  saving,
  error,
  ok,
  onSave,
  onReset,
  readOnly,
}: {
  dirty: boolean
  saving: boolean
  error?: string | null
  ok?: boolean
  onSave: () => void
  onReset: () => void
  readOnly?: boolean
}) {
  return (
    <div className="mt-[18px] flex flex-wrap items-center gap-3 rounded-card border border-line bg-card px-4 py-3 shadow-soft">
      {error ? (
        <span className="text-[12.5px] font-medium text-venetian">{error}</span>
      ) : ok ? (
        <span className="text-[12.5px] font-medium text-ok">Modifiche salvate.</span>
      ) : readOnly ? (
        <span className="text-[12px] text-muted">Sola lettura — modifica nel gestionale.</span>
      ) : (
        <span className="text-[11.5px] text-muted">
          {dirty ? 'Modifiche non salvate.' : 'Il salvataggio scrive sul gestionale live.'}
        </span>
      )}
      <div className="ml-auto flex items-center gap-2">
        <button
          onClick={onReset}
          disabled={saving || !dirty}
          className="rounded-lg border border-line bg-card px-4 py-2 text-[13px] font-semibold text-slate hover:border-faint disabled:opacity-40"
        >
          Annulla
        </button>
        <button
          onClick={onSave}
          disabled={saving || !dirty}
          className="inline-flex items-center gap-2 rounded-lg bg-verdigris px-4 py-2 text-[13px] font-semibold text-white hover:bg-verdigris-700 disabled:opacity-50"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Salva modifiche
        </button>
      </div>
    </div>
  )
}

/* ── field atoms ─────────────────────────────────────────────────── */

export function Field({
  label,
  hint,
  children,
  className,
}: {
  label: string
  hint?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <label className={cn('block', className)}>
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-[1.2px] text-muted">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11.5px] text-faint">{hint}</span>}
    </label>
  )
}

export const inputCls =
  'h-10 w-full rounded-lg border border-line bg-card px-3 text-[13.5px] text-coal outline-none placeholder:text-faint focus:border-verdigris disabled:opacity-50'

export function TextInput({
  value,
  onChange,
  disabled,
  placeholder,
  mono,
}: {
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  placeholder?: string
  mono?: boolean
}) {
  return (
    <input
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={cn(inputCls, mono && 'font-mono')}
    />
  )
}

export function NumberInput({
  value,
  onChange,
  disabled,
  min,
  step,
  suffix,
}: {
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  min?: number
  step?: number
  suffix?: string
}) {
  return (
    <div className="flex h-10 items-center rounded-lg border border-line bg-card px-3 focus-within:border-verdigris">
      <input
        type="number"
        value={value}
        min={min}
        step={step}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent text-[13.5px] text-coal outline-none disabled:opacity-50"
      />
      {suffix && <span className="ml-2 shrink-0 text-[12px] text-muted">{suffix}</span>}
    </div>
  )
}

export function CurrencyInput({
  value,
  onChange,
  disabled,
}: {
  value: string
  onChange: (v: string) => void
  disabled?: boolean
}) {
  return (
    <div className="flex h-10 items-center gap-1.5 rounded-lg border border-line bg-card px-3 focus-within:border-verdigris">
      <span className="text-[12px] text-muted">€</span>
      <input
        type="number"
        step="0.01"
        min="0"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent text-right font-mono text-[13.5px] text-coal outline-none disabled:opacity-50"
      />
    </div>
  )
}

export function SelectInput({
  value,
  onChange,
  options,
  disabled,
}: {
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
  disabled?: boolean
}) {
  return (
    <select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} className={inputCls}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

/** Masked secret input with a reveal toggle — for API keys / tokens. */
export function PasswordInput({
  value,
  onChange,
  disabled,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  placeholder?: string
}) {
  const [show, setShow] = useState(false)
  return (
    <div className="flex h-10 items-center rounded-lg border border-line bg-card px-3 focus-within:border-verdigris">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent font-mono text-[13px] text-coal outline-none disabled:opacity-50"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="ml-2 shrink-0 text-faint hover:text-slate"
        aria-label={show ? 'Nascondi' : 'Mostra'}
        tabIndex={-1}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  )
}

/** A labelled row with a switch on the right (for boolean settings). */
export function SwitchRow({
  title,
  desc,
  on,
  onToggle,
  disabled,
  aside,
}: {
  title: string
  desc?: ReactNode
  on: boolean
  onToggle?: (v: boolean) => void
  disabled?: boolean
  aside?: ReactNode
}) {
  return (
    <div className="flex items-center gap-4 border-b border-line-soft py-3.5 last:border-0">
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-semibold text-coal">{title}</div>
        {desc && <div className="mt-0.5 text-[12.5px] leading-snug text-muted">{desc}</div>}
      </div>
      {aside}
      <SwitchLocal on={on} disabled={disabled || !onToggle} onClick={() => onToggle?.(!on)} />
    </div>
  )
}

// Local copy of the pill switch so SwitchRow stays self-contained.
function SwitchLocal({ on, onClick, disabled }: { on: boolean; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'relative inline-flex h-[22px] w-[38px] shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed',
        on ? 'bg-verdigris' : 'bg-line',
        disabled && 'opacity-60',
      )}
    >
      <span
        className={cn(
          'inline-block h-[16px] w-[16px] rounded-full bg-white shadow transition-transform',
          on ? 'translate-x-[19px]' : 'translate-x-[3px]',
        )}
      />
    </button>
  )
}
